import { AxiosResponse } from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { createGlobalState } from 'react-use';
import { RestAPIEndpoints } from '@lifeomic/react-client';
import { useActiveProject } from '../../../../hooks';
import { useCarePlans } from '../../../../hooks/useCarePlans';
import { useApiClient } from '../../../../hooks/useRestAPIHooks';
import { KnownImpressionTypes } from '../AITools/ImpressionEditor/ImpressionItem';

type HealthSummaryInput = HealthSummaryV2Input;

type HealthSummaryInputBase = {
  subjectId: string;
  projectId: string;
  mutate?: boolean;
  skipSummaries?: boolean;
  skipActionItems?: boolean;
};

type HealthSummaryV2Input = HealthSummaryInputBase & {
  version: 'v2';
  crdId: string;
  modelId?: string;
  requestedSummaries?: KnownImpressionTypes[];
  instructions?: {
    summaryType: KnownImpressionTypes;
    instructions: string;
  }[];
};

type HealthSummaryInvocation = {
  id: string;
  status:
    | 'scheduled'
    | 'pending'
    | 'waiting'
    | 'processing'
    | 'failed'
    | 'completed'
    | 'expired';
  created: number;
  scheduled?: number | undefined;
  agentInput: unknown;
};
type HealthSummaryTask = {
  id: string;
  status: HealthSummaryInvocation['status'];
  created: number;
  scheduled?: number | undefined;
  agentInput?: AgentInput | unknown;
};

const isTaskResolved = (task: { status: HealthSummaryTask['status'] }) =>
  ['failed', 'completed', 'expired'].includes(task.status);

const useTask = createGlobalState<HealthSummaryTask | undefined>(undefined);

type HealthSummaryInvokeRequest =
  RestAPIEndpoints['POST /v1/agents-v2/health-summary/invoke']['Request'];
type HealthSummaryInvokeRequestV2 = Extract<
  HealthSummaryInvokeRequest,
  { version: 'v2' }
> & {
  model_id?: string;
};

export const useExecutiveSummaryGeneration = ({
  patientId,
  onGenerationSuccess,
  onGenerationFailure,
  onGenerationConflict,
  disableCheckForGeneratingSummary,
}: {
  patientId: string;
  onGenerationSuccess?: () => void;
  onGenerationFailure?: () => void;
  onGenerationConflict?: () => void;
  /**
   * If true, no check will be performed in the background to see
   * if a summary is already being generated for the member.
   */
  disableCheckForGeneratingSummary?: boolean;
}) => {
  const carePlanState = useCarePlans(patientId);
  const [project] = useActiveProject();
  const apiClient = useApiClient();

  const [isRefetching, setIsRefetching] = useState(false);

  const [task, setTask] = useTask();

  useEffect(() => {
    if (disableCheckForGeneratingSummary) {
      return;
    }
    // Check once if there is already an active task for this member.
    const checkForUpdates = async () => {
      const resp = (await apiClient.request(
        'GET /v1/agents-v2/health-summary/invocations',
        {
          subject_id: patientId,
        },
      )) as { data: HealthSummaryInvocation[] };
      if (resp?.data?.length > 0) {
        setTask(resp.data[0]);
      }
    };
    checkForUpdates();
    // Only run this once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // poll the active task for completion
  useEffect(() => {
    let intervalId: NodeJS.Timeout | undefined = undefined;

    const stopInterval = () => {
      intervalId && clearInterval(intervalId);
    };

    if (!task?.id) {
      return stopInterval();
    }

    const checkForUpdates = async () => {
      const response = (await apiClient.request(
        'GET /v1/agents-v2/health-summary/invocations/:task_id',
        {
          task_id: task.id,
        },
      )) as { data: HealthSummaryInvocation | undefined };
      const invocation = response.data;
      if (!invocation || isTaskResolved(invocation)) {
        stopInterval();
        setTask(undefined);
      } else {
        setTask(invocation);
      }
      if (!invocation || invocation.status === 'completed') {
        setTimeout(() => {
          // refetch the care plan to get the new resources
          // maybe poll instead of waiting for a second?
          setIsRefetching(true);
          carePlanState.refetchCarePlans();
          onGenerationSuccess?.();
        }, 1000);
      } else if (invocation.status === 'failed') {
        onGenerationFailure?.();
      }
    };

    intervalId = setInterval(checkForUpdates, 5000);
    checkForUpdates(); // Check immediately

    return stopInterval;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiClient, task?.id]);

  const triggerGeneration = useCallback(
    async (input: AgentInput) => {
      const agentInput = constructAgentInput({
        ...input,
        subjectId: patientId,
        projectId: project.id,
      });
      let res: AxiosResponse;
      if (task?.id) {
        res = await apiClient.request(
          'PATCH /v1/agents-v2/health-summary/scheduled/:task_id',
          {
            task_id: task.id,
          },
        );
      } else {
        try {
          const requestPayload = toHealthSummaryInvokeRequest(agentInput);
          res = await apiClient.request(
            'POST /v1/agents-v2/health-summary/invoke',
            requestPayload,
          );
        } catch (err: any) {
          if (err?.response?.status === 409) {
            onGenerationConflict?.();
            return;
          }
          throw err;
        }
      }
      const taskId = res.headers?.location?.split('/').pop();
      if (taskId) {
        setTask({
          id: taskId,
          status: 'pending',
          created: Date.now(),
          agentInput,
        });
      }
    },
    [apiClient, patientId, project.id, task, setTask, onGenerationConflict],
  );

  return {
    task,
    isRefetching,
    generateExecutiveSummary: triggerGeneration,
    isGenerating:
      task?.status === 'pending' ||
      task?.status === 'processing' ||
      task?.status === 'waiting',
  };
};

export type AgentInput = Pick<
  HealthSummaryV2Input,
  | 'crdId'
  | 'modelId'
  | 'requestedSummaries'
  | 'skipActionItems'
  | 'instructions'
>;

function constructAgentInput(
  agentInput: AgentInput & Pick<HealthSummaryInput, 'subjectId' | 'projectId'>,
): HealthSummaryInput {
  return { version: 'v2', ...agentInput };
}

function toHealthSummaryInvokeRequest(
  agentInput: HealthSummaryInput,
): HealthSummaryInvokeRequest {
  const request: HealthSummaryInvokeRequestV2 = {
    version: 'v2',
    subject_id: agentInput.subjectId,
    project_id: agentInput.projectId,
    crd_id: agentInput.crdId,
    ...(agentInput.modelId !== undefined
      ? { model_id: agentInput.modelId }
      : {}),
    ...(agentInput.mutate !== undefined ? { mutate: agentInput.mutate } : {}),
    ...(agentInput.requestedSummaries !== undefined
      ? { requested_summaries: agentInput.requestedSummaries }
      : {}),
    ...(agentInput.skipSummaries !== undefined
      ? { skip_summaries: agentInput.skipSummaries }
      : {}),
    ...(agentInput.skipActionItems !== undefined
      ? { skip_action_items: agentInput.skipActionItems }
      : {}),
    ...(agentInput.instructions !== undefined
      ? {
          instructions: agentInput.instructions.map(instruction => ({
            summary_type: instruction.summaryType,
            instructions: instruction.instructions,
          })),
        }
      : {}),
  };
  return request as HealthSummaryInvokeRequest;
}
